import React, { useRef } from "react";
import { Check, Minus, Plus } from "lucide-react";
import { cn } from "lib/utils";

const PRESETS = [
  { days: 1, label: "1 day" },
  { days: 2, label: "2 days" },
  { days: 3, label: "3 days" },
  { days: 7, label: "1 week" },
];
const MAX_DAYS = 365;

/** How the storefront words the lead time; kept in step with the product details drawer there. */
export function readyTimeText(days: number) {
  const date = new Date(Date.now() + days * 86400000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return `Ready about ${days} ${days === 1 ? "day" : "days"} after ordering (around ${date})`;
}

interface ReadyTimeFieldProps {
  /** Days between ordering and the product being ready; 0 means ready now. */
  value: number;
  onChange: (days: number) => void;
}

/**
 * Chooses whether a product is ready now or made to order, and for made-to-order products how many
 * days it takes. Saves as a number of days, with 0 for "ready now".
 */
export default function ReadyTimeField({ value, onChange }: ReadyTimeFieldProps) {
  const days = Number.isFinite(value) && value > 0 ? Math.min(Math.round(value), MAX_DAYS) : 0;
  const madeToOrder = days > 0;
  // Remembers the last lead time so switching to "Ready now" and back doesn't lose it.
  const lastDays = useRef(days || 1);
  if (days > 0) lastDays.current = days;

  const setDays = (next: number) => onChange(Math.max(1, Math.min(MAX_DAYS, Math.round(next) || 1)));

  const option = (selected: boolean, title: string, hint: string, onSelect: () => void) => (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex flex-1 items-start gap-3 rounded-[14px] border p-4 text-start transition-colors",
        selected ? "border-default-900 bg-card" : "border-default-200 bg-default-50 hover:border-default-400",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border",
          selected ? "border-default-900 bg-default-900 text-default-foreground" : "border-default-300 bg-card",
        )}
      >
        {selected && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span>
        <span className="block text-sm font-medium text-default-900">{title}</span>
        <span className="mt-0.5 block text-xs text-default-600">{hint}</span>
      </span>
    </button>
  );

  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="When is it ready?" className="flex flex-col gap-3 sm:flex-row">
        {option(!madeToOrder, "Ready now", "Customers can get it straight away.", () => onChange(0))}
        {option(madeToOrder, "Made to order", "It takes some days after an order.", () => setDays(lastDays.current))}
      </div>

      {madeToOrder && (
        <div className="space-y-3 rounded-[14px] bg-default-50 p-4">
          <div className="text-sm font-medium text-default-700">Ready in</div>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset.days}
                type="button"
                onClick={() => setDays(preset.days)}
                aria-pressed={days === preset.days}
                className={cn(
                  "h-9 rounded-full border px-4 text-sm font-medium transition-colors",
                  days === preset.days
                    ? "border-default-900 bg-default-900 text-default-foreground"
                    : "border-default-300 bg-card text-default-800 hover:border-default-900",
                )}
              >
                {preset.label}
              </button>
            ))}
            <div className="flex h-9 items-center rounded-full border border-default-300 bg-card">
              <button
                type="button"
                onClick={() => setDays(days - 1)}
                disabled={days <= 1}
                className="flex h-full w-9 items-center justify-center rounded-full text-default-700 hover:bg-default-100 disabled:opacity-40"
                aria-label="One day less"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_DAYS}
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                className="h-full w-12 bg-transparent text-center text-sm font-medium text-default-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="Days until ready"
              />
              <button
                type="button"
                onClick={() => setDays(days + 1)}
                disabled={days >= MAX_DAYS}
                className="flex h-full w-9 items-center justify-center rounded-full text-default-700 hover:bg-default-100 disabled:opacity-40"
                aria-label="One day more"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <span className="text-sm text-default-600">{days === 1 ? "day" : "days"}</span>
          </div>
          <p className="text-sm text-default-600">
            <span className="font-medium text-default-800">Customers will see:</span> {readyTimeText(days)}.
          </p>
        </div>
      )}
    </div>
  );
}
